import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { slotFromImageElement } from "@/lib/autoSlot";
import { reportSlotDefault } from "@/lib/slotDefaultsCapture";

// ── CmsAmbientCapture ────────────────────────────────────────────────────
//
// Runs on every page load. Scans the DOM for every editable <img>
// (identified via the 3-tier stable-slot lookup: data-cms-slot,
// data-cms-image-slot wrapper, scope+role composition) and reports
// each `{slot, currentSrc}` pair to the slot-defaults capture buffer.
//
// EditableImage already reports its own `fallback` prop, but many
// stable-slot images live outside <EditableImage> — SpaWellnessFeature,
// BespokeExperiencesSection hero, HeritageTimelineCarousel cards, etc.
// This ambient scanner picks those up so /admin/images has a thumbnail
// for every slot the site actually renders.
//
// A short polling loop lets lazy-loaded hero images (Intersection
// Observer + priority hints) finish mounting before we snapshot their
// src — otherwise we'd only see the initial blank state.
const AMBIENT_MAX_ATTEMPTS = 6;   // ~9s total at 1500ms cadence
const AMBIENT_INTERVAL_MS  = 1500;

const CmsAmbientCapture = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    let cancelled = false;
    let attempts = 0;

    const scan = () => {
      if (cancelled) return;
      attempts += 1;
      const imgs = document.querySelectorAll("img");
      imgs.forEach((img) => {
        // Skip anything explicitly opted-out of CMS editing.
        if (img.closest('[data-no-cms-edit="true"]')) return;
        // Small (< 64px) images are typically decorative icons —
        // /admin/images filters them out too.
        const rect = img.getBoundingClientRect();
        if (rect.width < 64 || rect.height < 64) return;
        const src = img.currentSrc || img.getAttribute("src");
        if (!src) return;
        // Resolve the slot id the exact same way the rest of the CMS
        // does — matches PreviewHighlighter + AutoImageEditor lookups.
        let slot;
        try {
          slot = slotFromImageElement(img);
        } catch (_e) {
          slot = null;
        }
        // Only capture STABLE slots — `auto.<hash>` fingerprints aren't
        // real defaults, they're legacy artefacts.
        if (!slot || slot.startsWith("auto.")) return;
        reportSlotDefault(slot, src, pathname);
      });
      if (attempts < AMBIENT_MAX_ATTEMPTS) {
        window.setTimeout(scan, AMBIENT_INTERVAL_MS);
      }
    };

    // First scan after the initial paint gives the browser time to
    // apply CSS + load above-the-fold images.
    const t = window.setTimeout(scan, 800);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [pathname]);

  return null;
};

export default CmsAmbientCapture;
